/**
 * OptimisationDefenseCards.jsx — RC NDORFLOW.CLOSED-LOOP P1
 *
 * Renders the two visible surfaces produced by
 * `ndor.analyze_optimisation_artifacts`:
 *
 *   1. "Why am I stronger now?"      → improvement_map
 *   2. "How do I defend this?"       → defensibility_map
 *
 * Props:
 *   - artifacts: the `optimisation_artifacts` object from the
 *     finalize-charge response. May be null (gate wasn't passed) OR
 *     `{ generated: false, ... }` (LLM failed → empty stub). UI
 *     handles both gracefully.
 *
 * Rendered inline inside the Wizard "Generated now" card. Both maps
 * are collapsible (default: collapsed) so the user opts in to read
 * the defence — keeps the success card scannable for users who just
 * want to grab the CV and go.
 */

import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Shield,
  TrendingUp,
  AlertTriangle,
  Sparkles,
  ShieldCheck,
} from 'lucide-react';

const SectionList = ({ items }) => (
  <ul className="space-y-1.5 text-[12px] text-ndor-ink leading-relaxed">
    {items.map((s, i) => (
      <li key={i} className="flex gap-2">
        <span className="text-ndor-ink-muted shrink-0">•</span>
        <span>{s}</span>
      </li>
    ))}
  </ul>
);

const KeyValueList = ({ items, k1, k2, label1, label2 }) => (
  <ul className="space-y-2 text-[12px] leading-relaxed">
    {items.map((row, i) => (
      <li key={i} className="border-l-2 border-ndor-edge pl-2.5 space-y-0.5">
        <div className="text-ndor-ink">
          <span className="text-[10px] uppercase tracking-[0.12em] text-ndor-ink-muted mr-1.5">
            {label1}
          </span>
          {row[k1]}
        </div>
        {row[k2] && (
          <div className="text-ndor-ink-secondary">
            <span className="text-[10px] uppercase tracking-[0.12em] text-ndor-ink-muted mr-1.5">
              {label2}
            </span>
            {row[k2]}
          </div>
        )}
      </li>
    ))}
  </ul>
);

const Subhead = ({ icon: Icon, children }) => (
  <h5 className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-ndor-ink-muted font-medium mb-1.5">
    {Icon && <Icon className="h-3 w-3" />}
    {children}
  </h5>
);

const ImprovementMap = ({ map }) => {
  const im = map || {};
  return (
    <div className="space-y-3" data-testid="optimisation-improvement-map">
      {im.headline && (
        <p className="text-[13px] text-ndor-ink leading-relaxed font-medium">
          {im.headline}
        </p>
      )}
      {typeof im.score_delta === 'number' && im.score_delta !== 0 && (
        <div className="text-[11px] text-ndor-success">
          NDOR CV verdict moved {im.score_delta > 0 ? '+' : ''}
          <span className="font-mono-ui tabular-nums">{im.score_delta}</span> points.
        </div>
      )}

      {Array.isArray(im.gaps_closed) && im.gaps_closed.length > 0 && (
        <div>
          <Subhead icon={Sparkles}>Gaps closed</Subhead>
          <ul className="space-y-2 text-[12px] leading-relaxed">
            {im.gaps_closed.map((g, i) => (
              <li
                key={i}
                className="border-l-2 border-ndor-success/40 pl-2.5 space-y-0.5"
                data-testid="optimisation-gap-closed-row"
              >
                <div className="text-ndor-ink font-medium">{g.gap}</div>
                {g.evidence && (
                  <div className="text-ndor-ink-secondary">
                    <span className="text-[10px] uppercase tracking-[0.12em] text-ndor-ink-muted mr-1.5">
                      Now reads
                    </span>
                    {g.evidence}
                  </div>
                )}
                {(g.before || g.after) && (
                  <div className="text-[11px] text-ndor-ink-muted">
                    <span className="opacity-70">Before:</span> {g.before || 'absent'} ·{' '}
                    <span className="opacity-70">After:</span> {g.after}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {Array.isArray(im.ats_alignment_gains) && im.ats_alignment_gains.length > 0 && (
        <div>
          <Subhead>ATS alignment gains</Subhead>
          <SectionList items={im.ats_alignment_gains} />
        </div>
      )}

      {Array.isArray(im.recruiter_objections_reduced) && im.recruiter_objections_reduced.length > 0 && (
        <div>
          <Subhead>Recruiter objections reduced</Subhead>
          <KeyValueList
            items={im.recruiter_objections_reduced}
            k1="objection" label1="Objection"
            k2="mitigation" label2="Now answered by"
          />
        </div>
      )}

      {im.strategic_repositioning && (
        <div>
          <Subhead>Strategic repositioning</Subhead>
          <p className="text-[12px] text-ndor-ink leading-relaxed">
            {im.strategic_repositioning}
          </p>
        </div>
      )}

      {Array.isArray(im.remaining_risks) && im.remaining_risks.length > 0 && (
        <div>
          <Subhead icon={AlertTriangle}>Remaining risks</Subhead>
          <SectionList items={im.remaining_risks} />
        </div>
      )}
    </div>
  );
};

const DefensibilityMap = ({ map }) => {
  const dm = map || {};
  return (
    <div className="space-y-3" data-testid="optimisation-defensibility-map">
      {dm.narrative_anchor && (
        <p className="text-[13px] text-ndor-ink leading-relaxed font-medium border-l-4 border-ndor-gold pl-3 py-1">
          {dm.narrative_anchor}
        </p>
      )}

      {Array.isArray(dm.fully_supported_claims) && dm.fully_supported_claims.length > 0 && (
        <div>
          <Subhead icon={ShieldCheck}>Fully supported claims</Subhead>
          <KeyValueList
            items={dm.fully_supported_claims}
            k1="claim" label1="Claim"
            k2="evidence" label2="Evidence in your CV"
          />
        </div>
      )}

      {Array.isArray(dm.transferable_claims) && dm.transferable_claims.length > 0 && (
        <div>
          <Subhead>Transferable claims</Subhead>
          <KeyValueList
            items={dm.transferable_claims}
            k1="claim" label1="Claim"
            k2="bridge_logic" label2="How to bridge"
          />
        </div>
      )}

      {Array.isArray(dm.moderate_risk_claims) && dm.moderate_risk_claims.length > 0 && (
        <div>
          <Subhead icon={AlertTriangle}>Moderate-risk claims</Subhead>
          <ul className="space-y-2 text-[12px] leading-relaxed">
            {dm.moderate_risk_claims.map((row, i) => (
              <li
                key={i}
                className="border-l-2 border-ndor-warning/40 pl-2.5 space-y-0.5"
                data-testid="optimisation-moderate-risk-row"
              >
                <div className="text-ndor-ink font-medium">{row.claim}</div>
                {row.mitigation && (
                  <div className="text-ndor-ink-secondary">
                    <span className="text-[10px] uppercase tracking-[0.12em] text-ndor-ink-muted mr-1.5">
                      If pushed back
                    </span>
                    {row.mitigation}
                  </div>
                )}
                {row.soften_alternative && (
                  <div className="text-[11px] text-ndor-ink-muted italic">
                    Or soften to: "{row.soften_alternative}"
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {Array.isArray(dm.claims_requiring_interview_support) && dm.claims_requiring_interview_support.length > 0 && (
        <div>
          <Subhead>Prepare a STAR story for</Subhead>
          <KeyValueList
            items={dm.claims_requiring_interview_support}
            k1="claim" label1="Claim"
            k2="interview_prep_tip" label2="Prep tip"
          />
        </div>
      )}

      {Array.isArray(dm.remaining_risks) && dm.remaining_risks.length > 0 && (
        <div>
          <Subhead icon={AlertTriangle}>Remaining risks</Subhead>
          <SectionList items={dm.remaining_risks} />
        </div>
      )}
    </div>
  );
};

const CollapsibleCard = ({ title, icon: Icon, defaultOpen = false, testid, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      className="rounded-md border border-ndor-edge bg-ndor-surface-alt/30"
      data-testid={testid}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-ndor-surface-alt/50 rounded-md transition-colors"
        data-testid={`${testid}-toggle`}
      >
        <span className="flex items-center gap-2 text-[12px] font-medium text-ndor-ink">
          {Icon && <Icon className="h-3.5 w-3.5 text-ndor-gold" />}
          {title}
        </span>
        {open ? (
          <ChevronUp className="h-3.5 w-3.5 text-ndor-ink-muted" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 text-ndor-ink-muted" />
        )}
      </button>
      {open && <div className="px-3 pb-3 pt-1">{children}</div>}
    </div>
  );
};

export const OptimisationDefenseCards = ({ artifacts }) => {
  // No artifacts at all (gate not passed, or pre-P1 variant) → render nothing.
  if (!artifacts) return null;

  // Empty stub from a failed LLM call → render the retry pill.
  if (artifacts.generated === false) {
    return (
      <div
        className="rounded-md border border-dashed border-ndor-edge px-3 py-2 text-[11px] text-ndor-ink-muted"
        data-testid="optimisation-artifacts-unavailable"
      >
        Defence report unavailable — please refresh in a moment.
      </div>
    );
  }

  const im = artifacts.improvement_map || {};
  const dm = artifacts.defensibility_map || {};
  const hasIm =
    im.headline ||
    (Array.isArray(im.gaps_closed) && im.gaps_closed.length > 0) ||
    im.strategic_repositioning;
  const hasDm =
    dm.narrative_anchor ||
    (Array.isArray(dm.fully_supported_claims) && dm.fully_supported_claims.length > 0);

  if (!hasIm && !hasDm) return null;

  return (
    <div className="space-y-2 pt-2 border-t border-ndor-edge/60">
      {hasIm && (
        <CollapsibleCard
          title="Why am I stronger now?"
          icon={TrendingUp}
          testid="optimisation-improvement-card"
        >
          <ImprovementMap map={im} />
        </CollapsibleCard>
      )}
      {hasDm && (
        <CollapsibleCard
          title="How do I defend this at interview?"
          icon={Shield}
          testid="optimisation-defensibility-card"
        >
          <DefensibilityMap map={dm} />
        </CollapsibleCard>
      )}
    </div>
  );
};

export default OptimisationDefenseCards;
